/**
 * Settlement flow completion configuration.
 * Defines the steps within each settlement phase, their ordering,
 * and which SFrames map to each step.
 *
 * This mirrors flowCompletionConfig.ts for the planning side.
 */

import type { SettlementFlowId } from './settlementFlowMap';

/** Point / goal instructional framing for a step or sub-task */
export interface SettlementGuidance {
  point: string;
  goal: string;
}

export type SettlementFormFieldType = 'text' | 'textarea' | 'select' | 'date';

export interface SettlementFormField {
  id: string;
  label: string;
  type: SettlementFormFieldType;
  placeholder?: string;
  options?: { value: string; label: string }[];
  hint?: string;
  /** Preferred rows for textarea */
  rows?: number;
}

export interface SettlementStep {
  /** Unique step ID (e.g., 'iad-1') */
  id: string;
  /** Step number within the phase */
  stepNumber: number;
  /** Display title */
  title: string;
  /** Short description */
  description: string;
  /** Category badge label (e.g., 'Secure Property') */
  category?: string;
  /** Groups steps on phase overview (e.g., "Court process") */
  sectionGroup?: string;
  /** Instructional framing shown at top of the step */
  guidance?: SettlementGuidance;
  /** Estimated completion time in minutes */
  estimatedMinutes?: number;
  /** SFrame routes that belong to this step */
  frames: string[];
  /** Sub-tasks within this step */
  subTasks?: SettlementSubTask[];
}

export interface SettlementSubTask {
  id: string;
  title: string;
  description?: string;
  guidance?: SettlementGuidance;
  /** Structured fields (saved with settlement frame data for the step) */
  formFields?: SettlementFormField[];
  /** Checklist items within the sub-task */
  checklistItems?: SettlementChecklistItem[];
}

export interface SettlementChecklistItem {
  id: string;
  title: string;
  description?: string;
  whatThisIs?: string;
  whyItMatters?: string;
  bulletPoints?: string[];
}

export interface SettlementPhaseConfig {
  flowId: SettlementFlowId;
  label: string;
  overviewFrame: string;
  steps: SettlementStep[];
}

/**
 * Full configuration for the "Immediately After Death" phase.
 * This is the first workflow phase and the most detailed in the plan.
 * Other phases follow the same structure and will be populated in their Build Phases.
 */
const iadConfig: SettlementPhaseConfig = {
  flowId: 'iad',
  label: 'Immediately After Death',
  overviewFrame: '/sframe-141',
  steps: [
    {
      id: 'iad-1',
      stepNumber: 1,
      title: 'Obtain Death Certificates',
      description: 'Request certified copies needed for legal, financial, and insurance tasks.',
      category: 'Find Legal Documents',
      //estimatedMinutes: 30,
      frames: [
        '/sframe-124',
        '/sframe-125',
        '/frame-20',
        '/frame-20-1',
        '/frame-244',
        '/frame-21',
        '/frame-22',
        '/frame-23',
        '/frame-24',
      ],
      subTasks: [
        {
          id: 'iad-1-a',
          title: 'Request Certified Copies',
          description: 'Order at least 10 certified copies from the funeral home or vital records.',
        },
        {
          id: 'iad-1-b',
          title: 'Distribute Copies',
          description: 'Provide copies to banks, insurers, and government agencies as needed.',
        },
      ],
    },
    {
      id: 'iad-2',
      stepNumber: 2,
      title: 'Secure the Residence',
      description: 'Protect the home, valuables, and access points.',
      category: 'Secure Property',
      //estimatedMinutes: 60,
      frames: [
        '/sframe-134',
        '/frame-26',
        '/frame-27',
        '/frame-29',
        '/frame-32',
        '/frame-35',
        '/frame-38',
        '/sframe-135',
        '/sframe-136',
      ],
      subTasks: [
        {
          id: 'iad-2-residence',
          title: 'Secure the residence',
          description:
            'Use the guided screens to lock down access, utilities, mail, insurance, security, and a final walk-through.',
          checklistItems: [
            {
              id: 'iad-2-sr-26',
              title: 'Entry points secured',
              description: 'Doors, windows, locks, and spare keys addressed.',
            },
            {
              id: 'iad-2-sr-27',
              title: 'Utilities reviewed',
              description: 'Shutoffs located and account changes planned.',
            },
            {
              id: 'iad-2-sr-29',
              title: 'Mail forwarding arranged',
              description: 'USPS and key senders updated.',
            },
            {
              id: 'iad-2-sr-32',
              title: 'Property insurance verified',
              description: 'Carrier notified and coverage documented.',
            },
            {
              id: 'iad-2-sr-35',
              title: 'Security system updated',
              description: 'Monitoring and contacts match executor needs.',
            },
            {
              id: 'iad-2-sr-38',
              title: 'Completion checklist finished',
              description: 'Valuables, perishables, pets, and photo documentation complete.',
            },
          ],
        },
      ],
    },
    {
      id: 'iad-3',
      stepNumber: 3,
      title: 'Secure and Maintain Vehicles',
      description: 'Locate, secure, and document any vehicles.',
      category: 'Secure Property',
      //estimatedMinutes: 30,
      frames: [
        '/sframe-137',
        '/frame-44',
        '/frame-47',
        '/frame-50',
        '/frame-53',
        '/frame-56',
      ],
      subTasks: [
        {
          id: 'iad-3-secure',
          title: 'Secure vehicles',
          checklistItems: [
            {
              id: 'iad-3-check-secure',
              title: 'Physical security & keys documented',
              description: 'Confirm each vehicle is locked, keys are accounted for, and storage location is recorded.',
            },
          ],
        },
        {
          id: 'iad-3-insurance',
          title: 'Vehicle insurance',
          checklistItems: [
            {
              id: 'iad-3-check-insurance',
              title: 'Insurance policies reviewed',
              description: 'Identify active policies, coverage dates, and whether any claims should be opened.',
            },
          ],
        },
        {
          id: 'iad-3-registration',
          title: 'Registration & title',
          checklistItems: [
            {
              id: 'iad-3-check-registration',
              title: 'Registration and title status recorded',
              description: 'Capture title location, lienholder (if any), and registration expiration by vehicle.',
            },
          ],
        },
        {
          id: 'iad-3-maintenance',
          title: 'Maintenance & operability',
          checklistItems: [
            {
              id: 'iad-3-check-maintenance',
              title: 'Maintenance and storage condition noted',
              description: 'Battery, tires, fuel, and upcoming service needs documented for each vehicle.',
            },
          ],
        },
        {
          id: 'iad-3-storage',
          title: 'Storage decisions',
          checklistItems: [
            {
              id: 'iad-3-check-storage',
              title: 'Short-term storage plan decided',
              description: 'Decide where each vehicle will stay until distribution or sale, and who has access.',
            },
          ],
        },
      ],
    },
    {
      id: 'iad-4',
      stepNumber: 4,
      title: 'Funeral or Cremation Services',
      description: 'Arrange services and coordinate logistics based on wishes.',
      category: 'Personal & Final Arrangements',
      //estimatedMinutes: 120,
      frames: ['/sframe-138', '/sframe-139'],
      subTasks: [
        {
          id: 'iad-4-provider',
          title: 'Funeral home or crematory',
          checklistItems: [
            {
              id: 'iad-4-check-provider',
              title: 'Selected and contacted funeral home or direct cremation provider',
              description: 'Record the business name, main phone, and primary director you are working with.',
            },
          ],
        },
        {
          id: 'iad-4-service-type',
          title: 'Service type',
          checklistItems: [
            {
              id: 'iad-4-check-service-type',
              title: 'Burial, cremation, or other disposition confirmed',
              description: 'Align arrangements with the deceased’s wishes and any pre-paid contracts.',
            },
          ],
        },
        {
          id: 'iad-4-details',
          title: 'Service details',
          checklistItems: [
            {
              id: 'iad-4-check-details',
              title: 'Date, venue, visitation, and officiant documented',
              description: 'Capture published obituary details, religious or cultural elements, and transportation.',
            },
          ],
        },
        {
          id: 'iad-4-costs',
          title: 'Costs',
          checklistItems: [
            {
              id: 'iad-4-check-costs',
              title: 'Estimated total and payments tracked',
              description: 'Note deposits, insurance assignments, and who is authorized to approve expenses.',
            },
          ],
        },
        {
          id: 'iad-4-attendees',
          title: 'Family & guests',
          checklistItems: [
            {
              id: 'iad-4-check-attendees',
              title: 'Key attendees and notification list prepared',
              description: 'Include out-of-town guests, clergy, musicians, and anyone speaking at the service.',
            },
          ],
        },
        {
          id: 'iad-4-docs',
          title: 'Paperwork',
          checklistItems: [
            {
              id: 'iad-4-check-docs',
              title: 'Contracts, permits, and invoices uploaded or filed',
              description: 'Store copies with the estate file for reimbursement and tax reporting.',
            },
          ],
        },
      ],
    },
    {
      id: 'iad-5',
      stepNumber: 5,
      title: 'Locate Important Documents',
      description: 'Find key legal, financial, and insurance documents.',
      category: 'Find Legal Documents',
      //estimatedMinutes: 90,
      frames: ['/sframe-126', '/sframe-127', '/sframe-128'],
      subTasks: [
        {
          id: 'iad-5-will',
          title: 'Will & probate filings',
          checklistItems: [
            {
              id: 'iad-5-check-will',
              title: 'Last will location and custodian recorded',
              description: 'Note whether the original is with an attorney, the court, or a personal safe.',
            },
          ],
        },
        {
          id: 'iad-5-trust',
          title: 'Trust instruments',
          checklistItems: [
            {
              id: 'iad-5-check-trust',
              title: 'Trust agreements and amendments located',
              description: 'Include any restatements, certification pages, and trustee contact sheets.',
            },
          ],
        },
        {
          id: 'iad-5-financial',
          title: 'Financial records',
          checklistItems: [
            {
              id: 'iad-5-check-financial',
              title: 'Statements, deeds of deposit, and tax returns gathered',
              description: 'Prioritize the most recent statements for every institution you can identify.',
            },
          ],
        },
        {
          id: 'iad-5-insurance',
          title: 'Insurance',
          checklistItems: [
            {
              id: 'iad-5-check-insurance',
              title: 'Life, health, and property policies located',
              description: 'Record policy numbers, carrier portals, and agent contact information.',
            },
          ],
        },
        {
          id: 'iad-5-deeds',
          title: 'Real property',
          checklistItems: [
            {
              id: 'iad-5-check-deeds',
              title: 'Deeds, mortgages, and HOA documents located',
              description: 'Note county recorder information if originals are held by a lender.',
            },
          ],
        },
        {
          id: 'iad-5-digital',
          title: 'Digital assets',
          checklistItems: [
            {
              id: 'iad-5-check-digital',
              title: 'Passwords, devices, and two-factor recovery documented',
              description: 'Follow platform terms of service; consider working with an IT-savvy family member.',
            },
          ],
        },
        {
          id: 'iad-5-safe',
          title: 'Safe deposit box',
          checklistItems: [
            {
              id: 'iad-5-check-safe',
              title: 'Safe deposit box bank, number, and access steps recorded',
              description: 'Confirm who is named on the rental agreement and what court order may be required.',
            },
          ],
        },
      ],
    },
    {
      id: 'iad-6',
      stepNumber: 6,
      title: 'Initial Death Notifications',
      description: "Notify close contacts and track communication.",
      category: 'Personal & Final Arrangements',
      //estimatedMinutes: 45,
      frames: ['/sframe-129', '/sframe-130'],
      subTasks: [
        {
          id: 'iad-6-a',
          title: 'Prepare your notification list',
          checklistItems: [
            {
              id: 'iad-6-a-1',
              title: 'Review contacts from the estate plan (planner frame 146)',
              description: 'Confirm names, roles, and reachability (email or phone).',
            },
            {
              id: 'iad-6-a-2',
              title: 'Add anyone missing from the plan',
              description: 'Include employers, clergy, caregivers, or neighbors who should hear directly.',
            },
            {
              id: 'iad-6-a-3',
              title: 'Assign categories (family, friends, employers, service providers)',
              description: 'Keeps outreach organized and helps with bulk messaging.',
            },
          ],
        },
        {
          id: 'iad-6-b',
          title: 'Send and record outreach',
          checklistItems: [
            {
              id: 'iad-6-b-1',
              title: 'Choose templates and delivery method per person or in bulk',
              description: 'Email, phone, or in person — record what was used.',
            },
            {
              id: 'iad-6-b-2',
              title: 'Update status (pending, sent, acknowledged)',
              description: 'Track who has been reached and who confirmed receipt.',
            },
            {
              id: 'iad-6-b-3',
              title: 'Maintain the communication log',
              description: 'Note date, channel, and any documents shared.',
            },
          ],
        },
      ],
    },
    //{
      // id: 'iad-7',
      // stepNumber: 7,
      // title: 'Obtain Allowance and Copy of the Will',
      // description: 'Get a copy of the will from the attorney or court and understand allowances.',
      //category: 'Find Legal Documents',
      //estimatedMinutes: 30,
      //frames: ['/sframe-131', '/sframe-132'],
    //},
  ],
};

/**
 * Placeholder configs for remaining phases.
 * Steps will be filled out in their respective Build Phases.
 */
const secureInventoryConfig: SettlementPhaseConfig = {
  flowId: 'secure-inventory',
  label: 'Secure & Inventory Assets',
  overviewFrame: '/sframe-143',
  /** Step order follows Figma Frame 272: titled & tangible property first, then accounts, then debts. */
  steps: [
    {
      id: 'si-4',
      stepNumber: 1,
      title: 'Inventory Real Estate & Vehicles',
      description: 'Document all real property and vehicles with titles, values, and lien status.',
      category: 'Secure Property',
      estimatedMinutes: 45,
      frames: ['/sframe-155', '/sframe-156'],
      subTasks: [
        {
          id: 'si-4-a',
          title: 'Real estate (structured inventory)',
          description:
            'Use the real estate worksheet for each parcel: address, ownership vesting, tax parcel ID, mortgage holder, and estimated value.',
        },
        {
          id: 'si-4-b',
          title: 'Vehicles (structured inventory)',
          description:
            'Use the vehicle worksheet for each asset: VIN, title certificate location, lienholder, registration status, and storage location.',
        },
      ],
    },
    {
      id: 'si-5',
      stepNumber: 2,
      title: 'Catalog Personal Property & Business Interests',
      description:
        'Capture high-value personal items separately from operating or ownership interests in a business.',
      category: 'Secure Property',
      estimatedMinutes: 60,
      frames: ['/sframe-160', '/sframe-161'],
      subTasks: [
        {
          id: 'si-5-a',
          title: 'Personal property & valuables',
          description:
            'Record jewelry, art, collections, electronics, and firearms with photos, approximate value, and current secure location.',
        },
        {
          id: 'si-5-b',
          title: 'Business interests',
          description:
            'List entities, ownership percentage, role in management, buy-sell agreements, and primary CPA or attorney contacts.',
        },
      ],
    },
    {
      id: 'si-1',
      stepNumber: 3,
      title: 'Financial Accounts & Assets',
      description: 'Review and document all financial accounts and tax records to understand what exists and what needs to be managed.',
      category: 'Financial Accounts',
      estimatedMinutes: 90,
      frames: ['/sframe-146'],
    },
    {
      id: 'si-3',
      stepNumber: 5,
      title: 'Identify All Liabilities & Creditors',
      description: 'Catalog all debts: mortgages, car loans, credit cards, medical bills, personal loans.',
      category: 'Debts & Taxes',
      estimatedMinutes: 60,
      frames: ['/sframe-150', '/sframe-151'],
      subTasks: [
        {
          id: 'si-3-a',
          title: 'Pull Credit Reports',
          description: 'Request reports from all three bureaus using authorized executor credentials.',
          checklistItems: [
            { id: 'si-3-a-1', title: 'Equifax report saved to estate folder' },
            { id: 'si-3-a-2', title: 'Experian report saved to estate folder' },
            { id: 'si-3-a-3', title: 'TransUnion report saved to estate folder' },
          ],
        },
        {
          id: 'si-3-b',
          title: 'Secured & mortgage debt detail (Figma 289-302)',
          description: 'Complete the secured debt worksheets for each lien tied to real or titled property.',
        },
        {
          id: 'si-3-c',
          title: 'Revolving & unsecured debt detail (Figma 303-319)',
          description: 'Complete the unsecured worksheets for cards, medical, student, and personal obligations.',
        },
        {
          id: 'si-3-d',
          title: 'Cross-check with planner liabilities',
          description: 'Compare worksheets against any debts captured during estate planning (Frame 235).',
        },
      ],
    },
    {
      id: 'si-6',
      stepNumber: 6,
      title: 'Legal & Important Documents',
      description: 'Locate and organize legal documents and important records needed to manage and settle the estate.',
      category: 'Legal Documents',
      estimatedMinutes: 45,
      frames: ['/sframe-162'],
      subTasks: [],
    },
    {
      id: 'si-7',
      stepNumber: 7,
      title: 'Pets & Dependents',
      description: 'Review and manage care for pets and dependents, including who is responsible and what needs to be handled.',
      category: 'Care',
      estimatedMinutes: 30,
      frames: ['/sframe-163'],
      subTasks: [],
    },
  ],
};

const probateConfig: SettlementPhaseConfig = {
  flowId: 'probate',
  label: 'Probate',
  overviewFrame: '/sframe-170',
  // Simplified to two steps to match executor mental model and product decision.
  steps: [
    {
      id: 'prob-1',
      stepNumber: 1,
      title: 'Determine If Probate Is Needed',
      description:
        'Review the estate, ownership of assets, and existing documents to determine whether probate is required.',
      category: 'Probate',
      estimatedMinutes: 45,
      frames: ['/sframe-171'],
      guidance: {
        point: 'Decide whether formal probate is required for this estate.',
        goal: 'Confirm probate requirement and identify the appropriate court path or simplified affidavit option.',
      },
      subTasks: [],
    },
    {
      id: 'prob-2',
      stepNumber: 2,
      title: 'Apply for Legal Authority',
      description:
        'Gather documents, prepare court filings, and obtain the authority required to act on behalf of the estate.',
      category: 'Probate',
      estimatedMinutes: 120,
      frames: ['/sframe-172'],
      guidance: {
        point: 'Prepare and submit the filings needed to obtain legal authority.',
        goal: 'File required petitions, capture case numbers, and retain proof of authority.',
      },
      subTasks: [],
    },
  ],
};

/*
  Remapping notes for removed items:
  - Public Notice (previously prob-5) is surfaced in the Notify Interested Parties phase.
  - Estate Bank Account & Cash Management (previously prob-6) becomes part of post-authority workflows (post-approval banking and EIN tasks).
  - Professional Support and Verification of Authority are embedded into Step 2 (Apply for Legal Authority).
*/

const notifyConfig: SettlementPhaseConfig = {
  flowId: 'notify',
  label: 'Notify Interested Parties',
  overviewFrame: '/sframe-180',
  steps: [
    {
      id: 'not-1',
      stepNumber: 1,
      title: 'Official Notifications',
      description: 'Notify government agencies and fulfill legal creditor notification requirements.',
      category: 'Notifications',
      estimatedMinutes: 45,
      guidance: {
        point: 'Start with the agencies that trigger records updates and benefit changes.',
        goal: 'Prevent overpayments, update official records, and begin the creditor claims period.',
      },
      frames: ['/sframe-180', '/sframe-181'],
      subTasks: [
        {
          id: 'not-1-a',
          title: 'Government agencies',
          description: 'Update the agencies that control benefits and official records.',
          checklistItems: [
            {
              id: 'not-1-a-1',
              title: 'Social Security Administration',
              whatThisIs: 'Notify SSA of the death.',
              whyItMatters: 'Prevents overpayments and updates benefits.',
              bulletPoints: ['Report the death', 'Confirm benefits are stopped', 'Ask about survivor benefits'],
            },
            {
              id: 'not-1-a-2',
              title: 'Veterans Affairs (if applicable)',
              whatThisIs: 'Notify the VA of the death.',
              whyItMatters: 'Ensures benefits are handled correctly.',
              bulletPoints: ['Contact VA benefits office', 'Submit required documentation', 'Check for survivor benefits'],
            },
            {
              id: 'not-1-a-3',
              title: 'Medicare / Medicaid',
              whatThisIs: 'Notify healthcare programs.',
              whyItMatters: 'Avoids billing and coverage issues.',
              bulletPoints: ['Report the death', 'Confirm coverage status', 'Review outstanding claims'],
            },
            {
              id: 'not-1-a-4',
              title: 'State agencies (DMV, voter registration)',
              whatThisIs: 'Update state records.',
              whyItMatters: 'Prevents identity misuse.',
              bulletPoints: ['Cancel driver’s license', 'Update voter registration', 'Notify relevant agencies'],
            },
          ],
        },
        {
          id: 'not-1-b',
          title: 'Notify known creditors',
          description: 'Start the legal claims process and contact creditors directly.',
          checklistItems: [
            {
              id: 'not-1-b-1',
              title: 'Publish legal notice to creditors',
              whatThisIs: 'Publicly notify creditors.',
              whyItMatters: 'Starts the legal claims period.',
              bulletPoints: ['Publish notice per state rules', 'Keep proof of publication', 'Track deadlines'],
            },
            {
              id: 'not-1-b-2',
              title: 'Notify known creditors',
              whatThisIs: 'Contact known creditors directly.',
              whyItMatters: 'Ensures debts are properly addressed.',
              bulletPoints: ['Notify each creditor', 'Provide documentation if needed', 'Track responses'],
            },
          ],
        },
      ],
    },
    {
      id: 'not-2',
      stepNumber: 2,
      title: 'Financial & Insurance Notifications',
      description: 'Notify financial institutions and claim insurance or employment-related benefits.',
      category: 'Notifications',
      estimatedMinutes: 60,
      guidance: {
        point: 'Work through accounts that hold cash, investments, and insurance benefits.',
        goal: 'Secure assets, file claims, and capture account values needed for administration.',
      },
      frames: ['/sframe-183'],
      subTasks: [
        {
          id: 'not-2-a',
          title: 'Financial institutions',
          description: 'Update the institutions that hold estate cash and investments.',
          checklistItems: [
            {
              id: 'not-2-a-1',
              title: 'Banks & credit unions',
              whatThisIs: 'Notify banks of the death.',
              whyItMatters: 'Secures accounts and prevents unauthorized activity.',
              bulletPoints: ['Contact each bank', 'Freeze or update accounts', 'Request date-of-death balances'],
            },
            {
              id: 'not-2-a-2',
              title: 'Investment & retirement accounts',
              whatThisIs: 'Notify investment providers.',
              whyItMatters: 'Enables transfers and beneficiary claims.',
              bulletPoints: ['Contact brokerage or custodian', 'Confirm beneficiaries', 'Request statements'],
            },
          ],
        },
        {
          id: 'not-2-b',
          title: 'Insurance and employment benefits',
          description: 'Claim insurance and employer-related financial benefits.',
          checklistItems: [
            {
              id: 'not-2-b-1',
              title: 'Insurance companies',
              whatThisIs: 'File insurance claims.',
              whyItMatters: 'Provides financial support to beneficiaries.',
              bulletPoints: ['Contact insurers', 'Submit claim forms', 'Track payouts'],
            },
            {
              id: 'not-2-b-2',
              title: 'Employer-related financial benefits',
              whatThisIs: 'Collect employer-provided benefits.',
              whyItMatters: 'Ensures all benefits are received.',
              bulletPoints: ['Contact employer HR', 'Request final paycheck', 'Review retirement or insurance benefits'],
            },
          ],
        },
      ],
    },
    {
      id: 'not-3',
      stepNumber: 3,
      title: 'Accounts & Services',
      description: 'Manage employer, personal, and service-related accounts.',
      category: 'Notifications',
      estimatedMinutes: 45,
      guidance: {
        point: 'Close out the accounts and services that can create missed bills or digital risk.',
        goal: 'Avoid service interruptions, preserve access, and prevent unnecessary charges.',
      },
      frames: ['/sframe-186'],
      subTasks: [
        {
          id: 'not-3-a',
          title: 'Employer notices',
          description: 'Handle wage, benefit, and business-related follow-up.',
          checklistItems: [
            {
              id: 'not-3-a-1',
              title: 'Current employer',
              whatThisIs: 'Notify current employer.',
              whyItMatters: 'Ensures final compensation and benefits are handled.',
              bulletPoints: ['Inform employer', 'Collect final paycheck', 'Review benefits'],
            },
            {
              id: 'not-3-a-2',
              title: 'Former employers',
              whatThisIs: 'Contact prior employers.',
              whyItMatters: 'Identifies pensions or deferred benefits.',
              bulletPoints: ['Reach out to HR', 'Confirm eligibility', 'Request details'],
            },
            {
              id: 'not-3-a-3',
              title: 'Business partners / co-owners',
              whatThisIs: 'Notify business relationships.',
              whyItMatters: 'Ensures continuity and proper handling of interests.',
              bulletPoints: ['Notify partners', 'Review agreements', 'Determine next steps'],
            },
          ],
        },
        {
          id: 'not-3-b',
          title: 'Mail and subscriptions',
          description: 'Redirect mail and cancel recurring services.',
          checklistItems: [
            {
              id: 'not-3-b-1',
              title: 'USPS mail forwarding',
              whatThisIs: 'Redirect mail.',
              whyItMatters: 'Prevents missed bills and notices.',
              bulletPoints: ['Set up mail forwarding', 'Monitor incoming mail', 'Update key institutions'],
            },
            {
              id: 'not-3-b-2',
              title: 'Cancel subscriptions',
              whatThisIs: 'Stop recurring services.',
              whyItMatters: 'Prevents unnecessary charges.',
              bulletPoints: ['Review statements', 'Cancel subscriptions', 'Confirm cancellations'],
            },
          ],
        },
        {
          id: 'not-3-c',
          title: 'Digital and utilities',
          description: 'Handle social media accounts and household services.',
          checklistItems: [
            {
              id: 'not-3-c-1',
              title: 'Social media accounts',
              whatThisIs: 'Close or memorialize accounts.',
              whyItMatters: 'Protects identity and digital presence.',
              bulletPoints: ['Review platform policies', 'Memorialize or close accounts', 'Secure login access'],
            },
            {
              id: 'not-3-c-2',
              title: 'Utilities & services',
              whatThisIs: 'Manage household services.',
              whyItMatters: 'Avoids disruption or unnecessary costs.',
              bulletPoints: ['Transfer or cancel services', 'Maintain essential utilities', 'Update account holders'],
            },
          ],
        },
      ],
    },
  ],
};

const debtsTaxesConfig: SettlementPhaseConfig = {
  flowId: 'debts-taxes',
  label: 'Settle Debts, Expenses & Taxes',
  overviewFrame: '/sframe-198',
  steps: [
    {
      id: 'dt-1',
      stepNumber: 1,
      title: 'Review & Organize All Debts',
      description:
        'Compile a complete inventory of debts, align with design screens 298–330 & 303, and compare to planner debts (frame-235).',
      category: 'Debts & Taxes',
      estimatedMinutes: 60,
      frames: ['/sframe-198', '/sframe-199'],
      subTasks: [
        {
          id: 'dt-1-figma',
          title: 'Design workflow: Review & organize',
          checklistItems: [
            { id: 'dt-1-f328', title: 'Screen 328 — Estate debt dashboard', description: 'Confirm all known obligations appear on the working list.' },
            { id: 'dt-1-f333', title: 'Screen 333 — Creditor contact sheet', description: 'Capture mailing address, account number, and phone for each creditor.' },
            { id: 'dt-1-f298', title: 'Screen 298 — Debt categories', description: 'Tag each debt: secured, administrative, tax, unsecured, disputed.' },
            { id: 'dt-1-f329', title: 'Screen 329 — Supporting documents index', description: 'List statements, contracts, and notices you already hold.' },
            { id: 'dt-1-f330', title: 'Screen 330 — Collateral & liens', description: 'Note assets tied to secured debts (vehicles, real estate, UCC filings).' },
            { id: 'dt-1-f303', title: 'Screen 303 — Expense vs debt split', description: 'Separate recurring bills (utilities) from true creditor claims.' },
            { id: 'dt-1-p235', title: 'Planner frame-235 — Import debts', description: 'Pull read-only planner debts into this phase; add any missing estate-only items.' },
            { id: 'dt-1-notes', title: 'Executor notes & flags', description: 'Flag disputed, uncertain, or joint debts for counsel review.' },
          ],
        },
      ],
    },
    {
      id: 'dt-2',
      stepNumber: 2,
      title: 'Plan Payment Order',
      description: 'Establish the legal payment priority sequence for all validated estate debts.',
      category: 'Debts & Taxes',
      estimatedMinutes: 45,
      frames: ['/sframe-202'],
      subTasks: [
        {
          id: 'dt-2-plan',
          title: 'Plan payment order',
          checklistItems: [
            { id: 'dt-2-p1', title: 'Understand legal priority', description: 'Review state priority statutes and identify secured vs unsecured priority.' },
            { id: 'dt-2-p2', title: 'Categorize debt priority', description: 'Flag administrative expenses, tax obligations, and general creditors.' },
            { id: 'dt-2-p3', title: 'Assess estate liquidity', description: 'Calculate available liquid assets vs total debts.' },
            { id: 'dt-2-p4', title: 'Draft payment schedule', description: 'Set statutory waiting period and sequence payments.' },
            { id: 'dt-2-p5', title: 'Finalize payment plan', description: 'Lock priority rankings and approve plan for execution.' },
          ],
        },
      ],
    },
    {
      id: 'dt-3',
      stepNumber: 3,
      title: 'Pay Validated Debts',
      description: 'Execute payments for all validated estate obligations in priority order.',
      category: 'Debts & Taxes',
      estimatedMinutes: 90,
      frames: ['/sframe-204'],
      subTasks: [
        {
          id: 'dt-3-pay',
          title: 'Pay validated debts',
          checklistItems: [
            { id: 'dt-3-p1', title: 'Final payment preparation', description: 'Confirm estate account balance, verify creditor payment methods, review no-pay list.' },
            { id: 'dt-3-p2', title: 'High-priority payments', description: 'Pay administrative & legal fees, settle funeral costs, record confirmations.' },
            { id: 'dt-3-p3', title: 'Tax & government settlements', description: 'Submit federal estate tax, pay state/local taxes, obtain clearance.' },
            { id: 'dt-3-p4', title: 'General creditor distributions', description: 'Issue pro-rata payments, settle credit cards, pay medical & utility bills.' },
            { id: 'dt-3-p5', title: 'Payment documentation & closing', description: 'Collect release-of-claim forms, compile final ledger, notify heirs.' },
          ],
        },
      ],
    },
    {
      id: 'dt-4',
      stepNumber: 4,
      title: 'Identify Tax Filings',
      description: 'Determine all required federal and state tax filings for the estate.',
      category: 'Tax Filings',
      estimatedMinutes: 60,
      frames: ['/sframe-207'],
      subTasks: [
        {
          id: 'dt-4-tax',
          title: 'Identify tax filings',
          checklistItems: [
            { id: 'dt-4-t1', title: 'Determine final personal income tax', description: 'Verify last filed return, identify income sources, determine filing status.' },
            { id: 'dt-4-t2', title: 'Assess estate income tax requirements', description: 'Check estate income threshold, identify Form 1041 need, apply for EIN.' },
            { id: 'dt-4-t3', title: 'Evaluate federal estate (death) tax', description: 'Calculate gross estate value, check filing threshold, assess portability.' },
            { id: 'dt-4-t4', title: 'Identify state-level tax obligations', description: 'Review state estate tax laws, check inheritance tax, verify state final income tax.' },
            { id: 'dt-4-t5', title: 'Finalize tax calendar & professional help', description: 'Create deadline calendar, assign tax professionals, upload tax requirement summary.' },
          ],
        },
      ],
    },
    {
      id: 'dt-5',
      stepNumber: 5,
      title: 'Complete Tax Filings',
      description: 'File all required returns and obtain necessary clearances to close the estate.',
      category: 'Tax Filings',
      estimatedMinutes: 45,
      frames: ['/sframe-215'],
      subTasks: [],
    },
  ],
};

const distributeConfig: SettlementPhaseConfig = {
  flowId: 'distribute',
  label: 'Distribute Assets',
  overviewFrame: '/sframe-220',
  steps: [
    {
      id: 'dist-1',
      stepNumber: 1,
      title: 'Prepare Assets for Distribution',
      description: 'Maintain and prepare assets so they can be transferred, sold, or distributed.',
      category: 'All',
      estimatedMinutes: 45,
      frames: ['/sframe-221'],
      subTasks: [
        {
          id: 'dist-1-checklist',
          title: 'Work through each area to protect assets and prepare for distribution decisions.',
          checklistItems: [
            {
              id: 'dist-1-p1',
              title: 'Asset Readiness',
              whatThisIs: 'Ensure all assets are protected and maintained before distribution.',
              whyItMatters: 'Prevents loss in value and avoids delays when transferring or selling assets.',
              bulletPoints: [
                'Secure all properties and physical assets',
                'Maintain insurance coverage across all assets',
                'Keep required utilities active where needed',
              ],
            },
            {
              id: 'dist-1-p2',
              title: 'Ongoing Financial Obligations',
              whatThisIs: 'Continue required payments tied to estate assets.',
              whyItMatters: 'Missed payments can reduce asset value or create additional liabilities.',
              bulletPoints: [
                'Continue mortgage and loan payments',
                'Maintain critical services tied to assets',
                'Avoid lapses that impact ownership or value',
              ],
            },
            {
              id: 'dist-1-p3',
              title: 'Income & Asset Activity',
              whatThisIs: 'Track and manage income generated by estate assets.',
              whyItMatters: 'Ensures all income is accounted for and properly distributed later.',
              bulletPoints: [
                'Collect income from rentals, businesses, or investments',
                'Deposit all income into the estate account',
                'Keep a record of income activity for later accounting',
              ],
            },
            {
              id: 'dist-1-p4',
              title: 'Prepare for Distribution Decisions',
              whatThisIs: 'Identify how each asset will be handled in the next steps.',
              whyItMatters: 'Clarifies whether assets will be transferred, sold, or held.',
              bulletPoints: [
                'Identify assets that will be transferred directly',
                'Identify assets that will be sold',
                'Note any maintenance, repairs, or actions needed before distribution',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'dist-2',
      stepNumber: 2,
      title: 'Create Distribution Plan',
      description: 'Define who receives what based on the will and estate status.',
      category: 'All',
      estimatedMinutes: 30,
      frames: ['/sframe-222', '/sframe-224'],
    },
    {
      id: 'dist-3',
      stepNumber: 3,
      title: 'Distribute Personal Property',
      description: 'Transfer personal items and fulfill specific gifts.',
      category: 'Personal Property',
      estimatedMinutes: 30,
      frames: ['/sframe-230'],
    },
    {
      id: 'dist-4',
      stepNumber: 4,
      title: 'Distribute Financial Accounts',
      description: 'Transfer, close, or distribute bank, investment, and retirement accounts.',
      category: 'Financial Accounts',
      estimatedMinutes: 60,
      frames: ['/sframe-235'],
    },
    {
      id: 'dist-5',
      stepNumber: 5,
      title: 'Transfer or Sell Property, Vehicles & Businesses',
      description: 'Handle real estate, vehicles, and business interests that require transfer or sale.',
      category: 'Real Estate & Vehicles',
      estimatedMinutes: 60,
      frames: ['/sframe-240'],
    },
    {
      id: 'dist-6',
      stepNumber: 6,
      title: 'Special Distributions & Interim Payments',
      description: 'Manage charitable gifts, minor beneficiaries, and partial distributions.',
      category: 'Special Distributions',
      estimatedMinutes: 90,
      frames: ['/sframe-245'],
    },
  ],
};

const closeConfig: SettlementPhaseConfig = {
  flowId: 'close',
  label: 'Close the Estate',
  overviewFrame: '/sframe-250',
  steps: [
    {
      id: 'cls-1',
      stepNumber: 1,
      title: 'Finalize Accounting',
      description: 'Create a complete record of all income, expenses, and distributions, and confirm the estate balance is zero.',
      category: 'Closing',
      estimatedMinutes: 90,
      frames: ['/sframe-250', '/sframe-251', '/sframe-252'],
      subTasks: [
        {
          id: 'cls-1-checklist',
          title: 'Work through each item to ensure all funds are accounted for before closing the estate.',
          checklistItems: [
            { 
              id: 'cls-1-p1', 
              title: 'Record all income', 
              whatThisIs: 'Capture all money received by the estate.',
              whyItMatters: 'Ensures nothing is missing before final reporting.',
              bulletPoints:[
                'Identify all income sources (accounts, sales, refunds)',
                'Confirm deposits into the estate account',
                'Include insurance proceeds or final payments',
                'Verify no expected funds are outstanding'
              ]
            },
            {
              id: 'cls-1-p2',
              title: 'Pay all expenses and debts',
              whatThisIs: 'Confirm all bills, debts, and obligations have been paid.',
              whyItMatters: 'Outstanding liabilities can delay or block estate closure.',
              bulletPoints: [
                'Review all known debts and expenses',
                'Confirm payments have cleared',
                'Resolve any remaining balances',
                'Document final payments if needed'
              ]
            },
            {
              id: 'cls-1-p3',
              title: 'Complete all distributions',
              whatThisIs: 'Ensure all assets and funds have been distributed to beneficiaries.',
              whyItMatters: 'The estate cannot close until distributions are finalized.',
              bulletPoints: [
                'Confirm distributions match the will or plan',
                'Verify transfers were completed',
                'Ensure beneficiaries received their allocations',
                'Document any final transfers'
              ]
            },
            {
              id: 'cls-1-p4',
              title: 'Confirm estate account balance is zero',
              whatThisIs: 'Verify that no funds remain in the estate account.',
              whyItMatters: 'A zero balance is required to officially close the estate.',
              bulletPoints: [
                'Check final account balance',
                'Confirm all funds were distributed or used for expenses',
                'Resolve any remaining transactions',
                'Prepare to close the account'
              ]
            },
            {
              id: 'cls-1-p5',
              title: 'Review final accounting summary',
              whatThisIs: 'Do a final check of all financial activity.',
              whyItMatters: 'Ensures accuracy before sharing with beneficiaries or the court.',
              bulletPoints: [
                'Review totals (income, expenses, distributions)',
                'Confirm records are complete and accurate',
                'Save or export final accounting summary',
                'Prepare for beneficiary approvals'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'cls-2',
      stepNumber: 2,
      title: 'Obtain Beneficiary Approvals',
      description: 'Share the final accounting and confirm all beneficiaries approve the distributions.',
      category: 'Closing',
      estimatedMinutes: 30,
      frames: ['/sframe-253', '/sframe-254'],
      subTasks: [
        {
          id: 'cls-2-checklist',
          title: 'Work through each item to ensure approvals are collected before closing the estate.',
          checklistItems: [
            { id: 'cls-2-p1', 
              title: 'Share final accounting', 
              whatThisIs: 'Provide beneficiaries with a summary of all financial activity.',
              whyItMatters: 'Ensures transparency and gives beneficiaries a chance to review.',
              bulletPoints: [
                'Share final accounting summary',
                'Include explanation of distributions',
                'Provide supporting documents if needed',
                'Give time for review'
              ]
            },
            {
              id: 'cls-2-p2',
              title: 'Confirm beneficiary approvals',
              whatThisIs: 'Verify that beneficiaries agree with the accounting and distributions.',
              whyItMatters: 'Approval helps prevent disputes and is often required for closure.',
              bulletPoints: [
                'Follow up with beneficiaries',
                'Answer questions or concerns',
                'Confirm verbal or written approval',
                'Track approval status',
              ],
            },
            {
              id: 'cls-2-p3',
              title: 'Collect signed receipts and releases',
              whatThisIs: 'Obtain formal acknowledgment from beneficiaries.',
              whyItMatters: 'Protects you from future claims or disputes.',
              bulletPoints: [
                'Request signed approval documents',
                'Collect receipts for distributions',
                'Ensure documents are complete',
                'Store securely',
              ],
            },
            {
              id: 'cls-2-p4',
              title: 'Resolve any outstanding concerns',
              whatThisIs: 'Address any issues raised during review.',
              whyItMatters: 'Unresolved concerns can delay estate closure.',
              bulletPoints: [
                'Identify any objections or questions',
                'Work with beneficiaries to resolve',
                'Consult attorney if needed',
                'Confirm final agreement',
              ],
            },
          ]
        },
      ],
    },
    {
      id: 'cls-3',
      stepNumber: 3,
      title: 'Complete Final Filings & Close the Estate',
      description: 'Finalize all filings, close accounts, and obtain court discharge to officially complete the estate.',
      category: 'Closing',
      estimatedMinutes: 45,
      frames: ['/sframe-255'],
      subTasks: [
        {
          id: 'cls-3-checklist',
          title: 'Work through each item to ensure all legal, financial, and administrative steps are complete before closing.',
          checklistItems: [
            {
              id: 'cls-3-p1',
              title: 'Confirm all filings and obligations are complete',
              whatThisIs: 'Ensure all required legal and financial filings have been completed.',
              whyItMatters: 'Incomplete filings can delay or prevent estate closure.',
              bulletPoints: [
                'Verify all tax returns have been filed',
                'Confirm any required court filings are submitted',
                'Ensure outstanding obligations are resolved',
                'Review with attorney or advisor if needed',
              ],
            },
            {
              id: 'cls-3-p2',
              title: 'Complete final distributions',
              whatThisIs: 'Make sure all remaining assets have been distributed.',
              whyItMatters: 'The estate cannot be closed until all distributions are complete.',
              bulletPoints: [
                'Confirm all beneficiaries received distributions',
                'Verify amounts match the final plan',
                'Collect final receipts if needed',
                'Ensure no remaining assets are unaccounted for',
              ],
            },
            {
              id: 'cls-3-p3',
              title: 'Close estate accounts and EIN',
              whatThisIs: "Shut down the estate's financial and tax identity.",
              whyItMatters: 'Leaving accounts or EIN open can create future liability.',
              bulletPoints: [
                'Close estate bank account',
                'Confirm balance is zero',
                'Notify IRS to close EIN',
                'Ensure no remaining transactions are pending',
              ],
            },
            {
              id: 'cls-3-p4',
              title: 'File for court discharge',
              whatThisIs: 'Submit final documentation to formally close the estate with the court.',
              whyItMatters: 'This releases you from your role and legal responsibility.',
              bulletPoints: [
                'Prepare final petition for discharge',
                'Submit required documentation',
                'Attend hearing if required',
                'Track filing status',
              ],
            },
            {
              id: 'cls-3-p5',
              title: 'Obtain and store court discharge',
              whatThisIs: 'Receive official confirmation that the estate is closed.',
              whyItMatters: 'Provides legal proof that your responsibilities are complete.',
              bulletPoints: [
                'Obtain court order or discharge document',
                'Upload and store securely',
                'Share with relevant parties if needed',
                'Keep for long-term records',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'cls-4',
      stepNumber: 4,
      title: 'Archive Records',
      description: 'Store all financial, legal, and tax documents for future reference and required retention.',
      category: 'Closing',
      estimatedMinutes: 30,
      frames: ['/sframe-257', '/sframe-258', '/sframe-259'],
    },
  ],
};

/** Collect structured form field ids for a step (used when hydrating saved frame data). */
export function collectStepFormFieldIds(step: SettlementStep): string[] {
  const ids: string[] = [];
  for (const st of step.subTasks || []) {
    for (const f of st.formFields || []) ids.push(f.id);
  }
  return ids;
}

/**
 * All settlement phase configs, keyed by flowId.
 */
export const SETTLEMENT_PHASE_CONFIGS: Record<SettlementFlowId, SettlementPhaseConfig> = {
  onboarding: {
    flowId: 'onboarding',
    label: 'Onboarding',
    overviewFrame: '/sframe-111',
    steps: [],
  },
  iad: iadConfig,
  'secure-inventory': secureInventoryConfig,
  probate: probateConfig,
  notify: notifyConfig,
  'debts-taxes': debtsTaxesConfig,
  distribute: distributeConfig,
  close: closeConfig,
};

/**
 * Get the phase config for a given flow ID.
 */
export function getPhaseConfig(flowId: SettlementFlowId): SettlementPhaseConfig {
  return SETTLEMENT_PHASE_CONFIGS[flowId];
}
